import { useState } from 'react';
import Modal from './Modal';
import { paymentsApi, formatCurrencyWhole } from '../utils/api';
import { round2, roundWhole } from '../utils/money';
import { toast } from './Toast';
import { track, EVENTS } from '../utils/analytics';
import Icon from './Icon';
import { PAYMENT_METHOD_OPTIONS } from '../pages/SupplierManagementPage';
import { isCreditNoteDoc } from '../utils/docTypes';
import { formatIsoDayShort } from '../utils/dateFormat';

// Derived from the shared list, not hand-written: a local copy had drifted to
// omit הוראת קבע (so a standing-order payment could never be recorded by hand,
// only by auto-pay) and spelled צ׳ק with an ASCII apostrophe, storing a second
// variant of one method. Filtering and icon lookup both normalize apostrophe
// vs geresh, so historical rows in either spelling still match.
const METHODS = PAYMENT_METHOD_OPTIONS.map(o => o.value);
const todayISO = () => new Date().toISOString().split('T')[0];

// The date range arrives as ISO (the wire format) but this box is read by a
// bookkeeper, and every other date on screen is DD/MM/YY.
const fmtRangeDate = (s) => (s ? formatIsoDayShort(String(s)) : '…');

// Half a shekel — mirrors AMOUNT_TOLERANCE in server/lib/paymentAmount.js,
// which is the authority. Money here is shown and pre-filled in whole shekels
// (owner, 14/10/2026) while the document keeps its agorot, so paying ₪233 on a
// ₪232.80 invoice is paying it in full: no reason demanded, and the server
// records the exact amount owed. The 1e-9 keeps an exact .50 inclusive.
const AMOUNT_TOLERANCE = 0.5 + 1e-9;

// The field opens on what is owed in whole shekels — the figure a supplier is
// actually paid — through the same roundWhole the amounts on screen use, so
// the pre-fill and the "נותרו" line can never disagree. A legacy remainder
// under half a shekel would round to 0 and block the submit, so it opens on
// its exact agorot instead.
const toAmountInput = (n) => String(roundWhole(n) || round2(n));

// `approvalOffer` ({ monthLabel: 'MM/YYYY' } | null, from
// utils/paymentLifecycle.js's approvalOfferFor*): when set, the modal offers
// to mark that month מאושר לתשלום in the same write — the restaurant's "yes,
// pay this" that the payment lifecycle tracks separately from the payment
// itself. Unchecked by default: paying and approving are two decisions.
// `holdApproval`: a caller with other requests for that month gets a payment that
// approves it to send in turn.
export default function PaymentModal({ supplierId, supplierName, documentId, documentInfo, dateFrom, dateTo, approvalOffer = null, holdApproval = (send) => send(), onSuccess, onClose }) {
  // Supplier-level payments forward the active date filter so the backend can
  // scope which documents get a payment row. Per-doc payments ignore it.
  const isSupplierLevel = !documentId;

  // The amount is a PER-DOCUMENT capability only. Supplier-level "בוצע תשלום"
  // settles whatever each document still owes, one row apiece — there is no
  // single figure to edit, and splitting one typed number across N documents
  // would be the app guessing which invoice got which share. So that mode says
  // plainly what it does and points at the document row for partial payments.
  //
  // The field is judged against what is STILL OWED, not the document's total:
  // on a ₪3,200 invoice already part-paid ₪2,000 the natural next payment is
  // the ₪1,200 balance, and treating that as a ₪2,000 discrepancy would demand
  // a written justification for doing the obvious thing. `owed` falls back to
  // the total so a first payment behaves exactly as it always has.
  const docTotal = documentInfo?.total != null ? Number(documentInfo.total) : null;
  const owed = documentInfo?.remaining != null && Number.isFinite(Number(documentInfo.remaining))
    ? Number(documentInfo.remaining)
    : docTotal;
  const alreadyPaid = documentInfo?.paidTotal != null ? Number(documentInfo.paidTotal) : 0;
  // חשבונית זיכוי (owner, 2026-09-15): a credit always settles in full — there
  // is no partial credit, so there is nothing to type (the server ignores an
  // amount for one as well).
  const canSetAmount = !isSupplierLevel && Number.isFinite(owed) && owed > 0
    && !isCreditNoteDoc(documentInfo);

  const [form, setForm] = useState({ payment_method: METHODS[0], payment_date: todayISO(), notes: '' });
  // Kept as a STRING, not a number: the user has to be able to clear the field
  // to retype it, and a numeric state would snap an empty field back to 0.
  const [amountInput, setAmountInput] = useState(() => (canSetAmount ? toAmountInput(owed) : ''));
  const [saving, setSaving] = useState(false);
  // The offer as ticked. A caller can withdraw its offer while this is open (the
  // month is out, or couldn't be read), and a retry must still ask for what the box
  // says. The server only ensures the approval, so asking again can't undo it.
  const [tickedOffer, setTickedOffer] = useState(null);
  const shownOffer = approvalOffer || tickedOffer;

  const amountNum   = Number(amountInput);
  const amountValid = amountInput.trim() !== '' && Number.isFinite(amountNum) && amountNum > 0;
  const diff        = amountValid ? amountNum - owed : 0;
  const changed     = amountValid && Math.abs(diff) > AMOUNT_TOLERANCE;
  const over        = diff > AMOUNT_TOLERANCE;
  // A changed amount MUST say why. Enforced again server-side
  // (lib/paymentAmount.js) — this is the courtesy, not the gate.
  const reasonMissing = changed && !form.notes.trim();
  const blocked = canSetAmount && (!amountValid || reasonMissing);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (blocked) return;
    setSaving(true);
    try {
      const payload = {
        supplier_id: supplierId,
        document_id: documentId || null,
        ...form,
      };
      if (isSupplierLevel) {
        if (dateFrom) payload.date_from = dateFrom;
        if (dateTo)   payload.date_to   = dateTo;
      }
      // Only sent when the user could edit it. The server stores NULL ("full
      // amount") whenever this equals the document's total, so an untouched
      // field leaves the row indistinguishable from every historical one.
      if (canSetAmount) payload.amount = amountNum;
      // Only ever sent as an explicit true — the server treats anything else
      // as "no", so an absent offer can never approve by accident.
      if (tickedOffer) payload.approve_month = true;
      console.log('[PaymentModal] POST /api/payments payload =', payload);
      const send = () => paymentsApi.create(payload);
      const r = await (payload.approve_month ? holdApproval(send) : send());
      console.log('[PaymentModal] response =', r?.data);
      const count = r?.data?.count ?? 1;
      track(EVENTS.PAYMENT_RECORDED, {
        scope: isSupplierLevel ? 'supplier_level' : 'single_doc',
        payment_method: form.payment_method,
        doc_count: count,
        amount_changed: canSetAmount ? changed : false,
        month_approved: Boolean(r?.data?.month_approved),
      });
      const base = isSupplierLevel && count > 1
        ? `התשלום נרשם עבור ${count} מסמכים`
        : 'התשלום נרשם בהצלחה';
      toast(r?.data?.month_approved ? `${base} · החודש אושר לתשלום` : base);
      onSuccess();
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה ברישום תשלום', 'error');
    } finally {
      setSaving(false);
    }
  };

  // One note line under the amount field, in three states. Never more than one
  // sentence: this sits between the field and the reason box, and a paragraph
  // here pushes the actual next step off a phone screen.
  const amountNote = () => {
    if (!amountValid) {
      return <span style={{ color: '#b91c1c' }}>יש להזין סכום גדול מ-0</span>;
    }
    if (over) {
      return (
        <span style={{ color: '#b45309', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Icon name="alert" size={13} />
          {alreadyPaid > 0 ? 'גבוה מהיתרה ב-' : 'גבוה מסכום המסמך ב-'}{formatCurrencyWhole(diff)}
        </span>
      );
    }
    if (changed) {
      return <span style={{ color: 'var(--ink-muted)' }}>תשלום חלקי · יישארו {formatCurrencyWhole(-diff)}</span>;
    }
    // Naming what the default IS matters more once there are two things it
    // could be — the whole bill, or the balance left on a part-paid one.
    return (
      <span style={{ color: 'var(--ink-muted)' }}>
        {alreadyPaid > 0 ? 'כל היתרה' : 'מלוא סכום המסמך'}
      </span>
    );
  };

  return (
    <Modal onClose={onClose} title="בוצע תשלום" size={420} closeOnBackdrop={false}>

    {/* Context summary */}
    <div style={{ background: '#f7f9fc', borderRadius: 10, padding: '10px 16px', marginBottom: 20 }}>
      <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: (documentInfo || isSupplierLevel) ? 4 : 0 }}>{supplierName}</div>
      {documentInfo && (
        <>
          <div style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
            {documentInfo.doc_type}
            {documentInfo.doc_num  && ` · ${documentInfo.doc_num}`}
            {documentInfo.total    && ` · ${formatCurrencyWhole(documentInfo.total)}`}
          </div>
          {/* Only when there IS a history — on a fresh document this line
              would be noise restating a zero. */}
          {alreadyPaid > 0 && (
            <div style={{ fontSize: '0.85rem', color: '#b45309', marginTop: 4 }}>
              שולם עד כה {formatCurrencyWhole(alreadyPaid)} · נותרו {formatCurrencyWhole(owed)}
            </div>
          )}
        </>
      )}
      {isSupplierLevel && (
        <>
          <div style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
            {dateFrom || dateTo
              ? `תשלום עבור מסמכים בטווח ${fmtRangeDate(dateFrom)} עד ${fmtRangeDate(dateTo)}`
              : 'תשלום עבור כל המסמכים הפעילים של הספק'}
          </div>
          {/* The one thing this screen used to leave unsaid. Users read
              "בוצע תשלום" as "record a payment" and expected to name a
              figure; saying up front that this is the full amount — and
              where to go instead — is cheaper than a field that lies. */}
          <div style={{ fontSize: '0.85rem', color: 'var(--ink-muted)', marginTop: 6, paddingTop: 6, borderTop: '1px solid #e6ecf3' }}>
            נרשם על <strong>מלוא הסכום</strong> של כל המסמכים בטווח.
            <br />
            <span style={{ color: 'var(--ink-muted)' }}>לתשלום חלקי — רשמי תשלום על מסמך בודד בכרטיס הספק.</span>
          </div>
        </>
      )}
    </div>

    <form onSubmit={handleSubmit}>
      {canSetAmount && (
        <div className="form-group">
          <label htmlFor="payment-amount">סכום ששולם</label>
          {/* min/step/inputMode together give phones the decimal keypad and
              desktop the spinner, without either one validating on its own —
              `blocked` above is what actually gates the submit. */}
          <div style={{ position: 'relative' }}>
            <input
              id="payment-amount"
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              className="form-control"
              value={amountInput}
              onChange={e => setAmountInput(e.target.value)}
              style={{
                paddingInlineEnd: 34,
                borderColor: !amountValid ? '#dc2626' : over ? '#f59e0b' : undefined,
              }}
              aria-describedby="payment-amount-note"
            />
            <span aria-hidden="true" style={{
              position: 'absolute', insetInlineEnd: 12, top: '50%', transform: 'translateY(-50%)',
              color: '#9aa5b1', fontSize: '0.9rem', pointerEvents: 'none',
            }}>₪</span>
          </div>
          <div
            id="payment-amount-note"
            role="status"
            style={{
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
              fontSize: '0.8rem', marginTop: 6, minHeight: 18,
            }}
          >
            {amountNote()}
            {changed && (
              <button
                type="button"
                onClick={() => setAmountInput(toAmountInput(owed))}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: '#0f3460', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'underline',
                  fontFamily: 'inherit',
                }}
              >
                החזר לסכום המלא
              </button>
            )}
          </div>
        </div>
      )}
      <div className="form-group">
        <label>אמצעי תשלום</label>
        <select className="form-control" value={form.payment_method}
          onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}>
          {METHODS.map(m => <option key={m}>{m}</option>)}
        </select>
      </div>
      <div className="form-group">
        <label>תאריך תשלום</label>
        <input type="date" className="form-control" value={form.payment_date} required
          onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} />
      </div>
      <div className="form-group">
        {/* The notes field doubles as the reason box: one place to write,
            and the change is explained on the very row it belongs to. Its
            label carries the requirement rather than an error appearing
            after the fact. */}
        <label htmlFor="payment-notes">{changed ? 'סיבה לשינוי הסכום (חובה)' : 'הערות (אופציונלי)'}</label>
        <textarea
          id="payment-notes"
          className="form-control"
          rows={2}
          value={form.notes}
          required={changed}
          placeholder={changed ? 'למשל: הנחה שסוכמה, זיכוי, יתרה תשולם בהמשך' : 'הערות לגבי התשלום...'}
          style={{ borderColor: reasonMissing ? '#f59e0b' : undefined }}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
        />
        {reasonMissing && (
          <div style={{ fontSize: '0.8rem', color: '#b45309', marginTop: 6 }}>
            שינית את הסכום — נא לציין למה.
          </div>
        )}
      </div>
      {shownOffer && (
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500 }}>
            <input
              type="checkbox"
              checked={Boolean(tickedOffer)}
              onChange={e => setTickedOffer(e.target.checked ? shownOffer : null)}
            />
            <span>אשר לתשלום {shownOffer.multi ? 'לחודשים' : 'לחודש'} {shownOffer.monthLabel}</span>
          </label>
          <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', marginTop: 4 }}>
            יסומן "מאושר לתשלום" על שמך, ויירשם כהערה על הספק.
          </div>
        </div>
      )}
      <div className="modal-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
        <button type="submit" className="btn btn-success" disabled={saving || blocked}>
          {saving ? 'שומר...' : <><Icon name="check" size={16} /> אשר תשלום</>}
        </button>
      </div>
    </form>
    </Modal>
  );
}
